import React from 'react'
import {
  SectionCard,
  FilterGroupLayout,
  Loader,
  Button,
  Icon,
  Text,
  constants,
} from '@adc/ui-core'
import { FilterSchema } from '@adc/blueprint'
import type {
  BuyingCriteriaLocation,
  BuyingCriteriaData,
  BuyingCriteriaActions,
} from '@adc/blueprint'
import LocationSection from '../sections/LocationSection'
import NotesSection from '../sections/NotesSection'
import classnames from 'classnames/bind'
import styles from './styles.scss'

const cx = classnames.bind(styles)

const SECTION_META: Record<string, { title: string; description: string }> = {
  priceRange: {
    title: 'Your price range',
    description: "Set property est. market value range you're targeting.",
  },
  lookingFor: {
    title: "What you're looking for",
    description: '',
  },
  propertyDetails: {
    title: 'Property Details',
    description:
      "Set your must-haves here, and we'll refine your property matches as you explore.",
  },
}

export interface BuyingCriteriaContentProps {
  data: BuyingCriteriaData
  actions: BuyingCriteriaActions
  elmId?: string
  borderless?: boolean
}

/**
 * BuyingCriteriaContent — the full buying criteria form body.
 *
 * Sections:
 * 1. Location (async search + chips)
 * 2. Price Range (filter framework)
 * 3. What You're Looking For (filter framework — occupancy, sale type, property type)
 * 4. Property Details (filter framework — beds, baths, year built)
 * 5. Anything Else? (free-text notes)
 */
const BuyingCriteriaContent: React.FC<BuyingCriteriaContentProps> = ({
  data,
  actions,
  elmId = 'buybox-modal',
  borderless = false,
}) => {
  if (data.isLoading) {
    return (
      <div className={cx('modal-content', 'loading')}>
        <Loader active elmId={`${elmId}-loader`} />
        <p className={cx('status-text')}>Loading your buying criteria...</p>
      </div>
    )
  }

  if (data.loadFailed) {
    return (
      <div className={cx('modal-content', 'error')}>
        <Icon
          elmId={`${elmId}-error-icon`}
          name='circle-exclamation'
          iconStyle='solid'
          size='large'
          color={constants.ColorNames.Error}
        />
        <Text.Header level={4}>Unable to load your criteria</Text.Header>
        <p className={cx('status-text')}>
          We couldn&apos;t load your buying criteria. Please check your
          connection and try again.
        </p>
        <Button
          role='primary'
          elmId={`${elmId}-retry`}
          onClick={() => actions.readUserPreferences()}
        >
          Try Again
        </Button>
      </div>
    )
  }

  return (
    <div className={cx('modal-content')}>
      <SectionCard
        borderless={borderless}
        hideSectionDividers
        elmId={`${elmId}-location`}
        number={1}
        title='Where are you looking to buy?'
        description="Add areas you want to focus on. We'll prioritize properties in these locations."
        saveStatus={data.sectionStatus.location}
      >
        <LocationSection
          elmId={`${elmId}-location-search`}
          locations={data.criteria.locations}
          onChange={(locs: BuyingCriteriaLocation[]) =>
            actions.updateLocations(locs)
          }
        />
      </SectionCard>

      {data.filterData.filterDefinitions.map((groupDef, index) => {
        const groupData = data.filterData.filterViewModels[groupDef.name]
        if (!groupData) return null
        const meta = SECTION_META[groupDef.name] || {
          title: groupDef.label,
          description: '',
        }
        return (
          <SectionCard
            borderless={borderless}
            hideSectionDividers
            key={groupDef.name}
            elmId={`${elmId}-${groupDef.name}`}
            number={2 + index}
            title={meta.title}
            description={meta.description}
            saveStatus={data.sectionStatus[groupDef.name]}
          >
            <FilterGroupLayout
              elmId={`${elmId}-filter-${groupDef.name}`}
              groupDefinition={groupDef}
              groupData={groupData}
              noPadding
              onChange={(key: string, value: FilterSchema.ValueType) =>
                actions.updateFilter(groupDef.name, key, value)
              }
            />
          </SectionCard>
        )
      })}

      {/*
        MERCH-258: the notes card is intentionally NOT numbered. Design wants
        the final card's header to match the plain (unnumbered) header style
        used by the right-hand column, so no `number` prop is passed here.
      */}
      <SectionCard
        borderless={borderless}
        hideSectionDividers
        elmId={`${elmId}-notes`}
        title='Anything else?'
        description='Anything else you want us to prioritize or avoid?'
        saveStatus={data.sectionStatus.notes}
      >
        <NotesSection
          elmId={`${elmId}-notes-textarea`}
          notes={data.criteria.notes || ''}
          onChange={notes => actions.updateNotes(notes)}
        />
      </SectionCard>
    </div>
  )
}

export default BuyingCriteriaContent
